import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccess, pathIsArea } from "@/lib/access";
import {
  House,
  Users,
  Package,
  SignOut,
  ChartLineUp,
  Kanban,
  Storefront,
  Receipt,
  Cube,
  Gear,
  Bell,
  ShieldCheck,
  Calculator,
  Factory,
  Truck,
  ClipboardText,
  ShoppingCart,
} from "@phosphor-icons/react";

const SALES_NAV = [
  { to: "/sales/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/sales/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testId: "nav-pipeline" },
  { to: "/sales/orders", label: "Orders", icon: Package, testId: "nav-orders" },
  { to: "/sales/pricing", label: "Pricing", icon: Calculator, testId: "nav-pricing" },
];

const INVOICE_NAV = [
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", end: true },
  { to: "/invoices/follow-ups", label: "AR Follow-ups", icon: Bell, testId: "nav-ar" },
  { to: "/products", label: "Products", icon: Cube, testId: "nav-products" },
  { to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings" },
];

// Grouped in plant-flow order (buy → receive & store → make → check).
// `badge` names a key from /inventory/nav-counts; `badgeFor` is the permission
// that makes that count "your work", so a badge only shows to people who act on it.
const INVENTORY_NAV = [
  {
    items: [
      { to: "/inventory", label: "Dashboard", icon: House, testId: "nav-inv-dashboard", end: true },
    ],
  },
  {
    section: "Purchasing",
    items: [
      { to: "/inventory/vendors", label: "Vendors", icon: Storefront, testId: "nav-vendors" },
      { to: "/inventory/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, testId: "nav-inv-purchase-orders", badge: "pos_awaiting", badgeFor: "po.approve" },
      { to: "/inventory/procurement", label: "Procurement & QC", icon: Truck, testId: "nav-inv-procurement", badge: "grns_qc_pending", badgeFor: "qa.decide" },
    ],
  },
  {
    section: "Store",
    items: [
      { to: "/inventory/stock", label: "Stock Ledger", icon: Package, testId: "nav-inv-stock" },
      { to: "/inventory/items", label: "Item Master", icon: Cube, testId: "nav-inv-items" },
    ],
  },
  {
    section: "Production",
    items: [
      { to: "/inventory/production-runs", label: "Production Runs", icon: Factory, testId: "nav-inv-runs" },
      { to: "/inventory/requisitions", label: "Requisitions", icon: ClipboardText, testId: "nav-inv-requisitions", badge: "requisitions_awaiting", badgeFor: "material_request.approve" },
      { to: "/inventory/recipes", label: "Recipes", icon: Kanban, testId: "nav-inv-recipes" },
      { to: "/inventory/batch-calculator", label: "Batch Calculator", icon: Calculator, testId: "nav-inv-calc" },
    ],
  },
  {
    section: "Quality",
    items: [
      { to: "/inventory/iso", label: "ISO 22000 / CAPA", icon: ShieldCheck, testId: "nav-inv-iso" },
    ],
  },
];

const AREA_THEME = {
  sales: { active: "bg-blue-50 text-blue-700", bar: "bg-blue-600", badge: "bg-blue-600" },
  invoice: { active: "bg-rose-50 text-rose-700", bar: "bg-rose-600", badge: "bg-rose-600" },
  inventory: { active: "bg-emerald-50 text-emerald-800", bar: "bg-emerald-600", badge: "bg-amber-500" },
};

function useNavCounts(enabled, pathname) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Refetch on navigation so a badge clears soon after the user works the queue.
    api.get("/inventory/nav-counts")
      .then(({ data }) => !cancelled && setCounts(data || {}))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, pathname]);
  return counts;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { roles, can } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = roles.includes("admin");

  const inInvoice = pathIsArea(location.pathname, "invoice");
  const inSales = pathIsArea(location.pathname, "sales");
  const inInventory = pathIsArea(location.pathname, "inventory");
  const currentArea = inInventory ? "inventory" : inInvoice ? "invoice" : inSales ? "sales" : "sales";
  const groups =
    currentArea === "invoice"
      ? [{ items: INVOICE_NAV }]
      : currentArea === "inventory"
      ? INVENTORY_NAV
      : [{ items: SALES_NAV }];
  const theme = AREA_THEME[currentArea];
  const counts = useNavCounts(inInventory, location.pathname);

  const accentClass =
    currentArea === "invoice"
      ? "bg-rose-600"
      : currentArea === "inventory"
      ? "bg-emerald-600"
      : "bg-blue-600";
  const subtitle =
    currentArea === "invoice"
      ? "Finance Workspace"
      : currentArea === "inventory"
      ? "Inventory & ERP"
      : "Sales Workspace";
  const BrandIcon =
    currentArea === "invoice" ? Receipt : currentArea === "inventory" ? Package : ChartLineUp;

  return (
    <aside
      className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-slate-200">
        <button
          onClick={() => navigate(currentArea === "invoice" ? "/invoices" : currentArea === "inventory" ? "/inventory" : "/sales/dashboard")}
          className="flex items-center gap-2.5 group"
          data-testid="brand-logo-button"
        >
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accentClass}`}>
            <BrandIcon size={20} weight="bold" className="text-white" />
          </div>
          <div className="text-left">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-900 leading-none">
              SVRAF
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
              {subtitle}
            </div>
          </div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto" aria-label="Main">
        {groups.map(({ section, items }, gi) => (
          <div key={section || gi} role="group" aria-label={section}>
            {section && (
              <div
                className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400"
                data-testid={`nav-section-${section.toLowerCase()}`}
              >
                {section}
              </div>
            )}
            <div className="space-y-0.5">
              {items.map(({ to, label, icon: Icon, testId, end, badge, badgeFor }) => {
                const n = badge && can(badgeFor) ? counts[badge] || 0 : 0;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={testId}
                    className={({ isActive }) =>
                      "relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition " +
                      (isActive ? theme.active : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r ${theme.bar}`} aria-hidden />
                        )}
                        <Icon size={18} weight={isActive ? "fill" : "duotone"} className="shrink-0" />
                        <span className="flex-1 truncate">{label}</span>
                        {n > 0 && (
                          <span
                            className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold tabular-nums text-white flex items-center justify-center ${theme.badge}`}
                            data-testid={`${testId}-badge`}
                            aria-label={`${n} waiting for you`}
                          >
                            {n > 99 ? "99+" : n}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 pb-4 border-t border-slate-100 pt-3 space-y-1">
        {isAdmin && (
          <NavLink
            to="/admin/settings"
            data-testid="nav-admin-settings"
            className={({ isActive }) =>
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition " +
              (isActive
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
            }
          >
            <Gear size={18} weight="duotone" />
            Settings
          </NavLink>
        )}
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          {user?.picture && (
            <img
              src={user.picture}
              alt=""
              className="w-7 h-7 rounded-full border border-slate-200 shrink-0"
              data-testid="user-avatar"
            />
          )}
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-900 truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition"
        >
          <SignOut size={18} weight="duotone" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
